import { createBrowserRouter } from 'react-router'
import { PublicLayout } from './layouts/PublicLayout'
import { RouteError } from './RouteError'
import { PlaceholderPage } from '@/components/common/PlaceholderPage'
import { PORTAL_PAGES } from '@/features/portals/pages'

/** Lazy-load a page module's default export (each page is its own chunk). */
const page = (load) => async () => ({ Component: (await load()).default })

/** Lazy-load the portal shell + guard, so portal code never ships to public visitors. */
function portalRoute(role) {
  return {
    path: role,
    lazy: async () => {
      const [{ PortalLayout }, { RequireRole }] = await Promise.all([
        import('./layouts/PortalLayout'),
        import('./guards/RequireRole'),
      ])
      return {
        Component: () => (
          <RequireRole role={role}>
            <PortalLayout portalKey={role} />
          </RequireRole>
        ),
      }
    },
    children: PORTAL_PAGES[role].map(({ path, load, ...meta }) => ({
      ...(path ? { path } : { index: true }),
      ...(load ? { lazy: page(load) } : { element: <PlaceholderPage {...meta} /> }),
    })),
  }
}

/** A page that needs a signed-in user (any role), e.g. a booking both parties can open. */
const authed = (load) => ({
  lazy: async () => {
    const [{ RequireAuth }, { default: Page }] = await Promise.all([import('./guards/RequireRole'), load()])
    return {
      Component: () => (
        <RequireAuth>
          <Page />
        </RequireAuth>
      ),
    }
  },
})

const info = (name, props = {}) => ({
  lazy: async () => {
    const mod = await import('@/features/marketing/InfoPages')
    const Page = mod[name]
    return { Component: () => <Page {...props} /> }
  },
})

export const routes = [
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { index: true, lazy: page(() => import('@/features/marketing/HomePage')) },
          { path: 'explore', lazy: page(() => import('@/features/discovery/ExplorePage')) },
          { path: 'm/:slug', lazy: page(() => import('@/features/discovery/MentorProfilePage')) },
          { path: 'pricing', ...info('PricingPage') },
          { path: 'faq', ...info('FaqPage') },
          { path: 'about', ...info('AboutPage') },
          { path: 'terms', ...info('TermsPage') },
          { path: 'privacy', ...info('PrivacyPage') },
          { path: 'refund-policy', ...info('RefundPolicyPage') },
          { path: 'login', lazy: page(() => import('@/features/auth/LoginPage')) },
          { path: 'auth/complete', lazy: page(() => import('@/features/auth/AuthCompletePage')) },
          {
            path: 'onboarding/role',
            lazy: async () => {
              const [{ RequireAuth }, { default: Page }] = await Promise.all([
                import('./guards/RequireRole'),
                import('@/features/auth/RoleSelectionPage'),
              ])
              return {
                Component: () => (
                  <RequireAuth>
                    <Page />
                  </RequireAuth>
                ),
              }
            },
          },
          { path: 'forbidden', lazy: page(() => import('@/features/auth/ForbiddenPage')) },
          { path: 'design', lazy: page(() => import('@/features/design/DesignPage')) },
          { path: 'bookings/:bookingId', ...authed(() => import('@/features/bookings/BookingDetailPage')) },
          { path: 'payments/return', ...authed(() => import('@/features/payments/PaymentReturnPage')) },
          { path: '*', lazy: page(() => import('@/features/marketing/NotFoundPage')) },
        ],
      },
      // The call is full-screen: outside the public layout (no site header/footer).
      { path: 'session/:bookingId', ...authed(() => import('@/features/session/SessionPage')) },
      portalRoute('mentee'),
      portalRoute('mentor'),
      portalRoute('admin'),
    ],
  },
]

export const createRouter = () => createBrowserRouter(routes)
